import { useApolloClient } from '@apollo/client';
import { BuyerUI } from '@choco-admin/types';
import { Card, CardBody, CardHeader, Link as HeroLink } from '@heroui/react';
import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';

import { ChocoCheckbox } from '../../../../components/FormComponents';
import { useNotification } from '../../../../contexts/NotificationContext';
import { useBuyerDisableRolesMutationMutation } from '../../../../generated';

import { DisableRolesModal } from './DisableRolesModal';
import { EnableRolesModal } from './EnableRolesModal';

interface SettingsTabProps {
  buyer: BuyerUI;
}

export const SettingsTab: React.FC<SettingsTabProps> = ({ buyer }) => {
  const [rolesEnabled, setRolesEnabled] = useState(Boolean(buyer.rolesEnabled));
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [showEnableModal, setShowEnableModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const buyerLabel = `${buyer.name} (${buyer.referenceId})`;
  const { addNotification } = useNotification();
  const [disableRolesMutation] = useBuyerDisableRolesMutationMutation();

  const handleCheckboxChange = useCallback(() => {
    if (rolesEnabled) {
      setShowDisableModal(true);
    } else {
      setShowEnableModal(true);
    }
  }, [rolesEnabled]);

  const handleDisableRoles = useCallback(async () => {
    setIsSubmitting(true);
    try {
      const result = await disableRolesMutation({
        variables: { buyerId: buyer.id },
      });

      if (result.data?.buyerDisableRoles?.__typename === 'Buyer') {
        setRolesEnabled(false);
        setShowDisableModal(false);
        addNotification({
          title: 'Success',
          message: 'Roles & Permissions disabled successfully',
          type: 'success',
        });
      } else {
        const errorMessage =
          result.data?.buyerDisableRoles?.__typename === 'NotFoundError'
            ? 'Buyer not found'
            : 'Failed to disable roles';
        throw new Error(errorMessage);
      }
    } catch (error) {
      addNotification({
        title: 'Error',
        message:
          error instanceof Error
            ? error.message
            : 'Failed to disable roles. Please try again.',
        type: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [buyer.id, disableRolesMutation, addNotification]);

  const client = useApolloClient();

  // There is no `buyerEnableRoles` mutation in the schema. Enabling works
  // implicitly: assigning a `buyerAdmin` role (the modal's addAdmins call)
  // emits BuyerUserAddedTopic, and a backend SQS handler flips the buyer's
  // `rolesEnabled` to true asynchronously. By the time this callback runs the
  // admins are assigned, so we mirror the flip into the Apollo cache the same
  // way the legacy admin does, rather than waiting on the async backend write.
  const handleEnableRoles = useCallback(
    (_adminUserIds: string[]) => {
      client.cache.modify({
        id: client.cache.identify({ __typename: 'Buyer', id: buyer.id }),
        fields: { rolesEnabled: () => true },
      });
      setRolesEnabled(true);
      setShowEnableModal(false);
      addNotification({
        title: 'Success',
        message: 'Roles & Permissions enabled successfully',
        type: 'success',
      });
    },
    [buyer.id, client, addNotification]
  );

  return (
    <div className="max-w-2xl mx-auto">
      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold">Roles & Permissions</h3>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="bg-default-50 rounded-lg p-4">
            <p className="text-default-600 text-sm mb-3">{buyerLabel}</p>
            <ChocoCheckbox
              isSelected={rolesEnabled}
              onValueChange={handleCheckboxChange}
              isDisabled={isSubmitting}
              size="sm"
              label="Enable Roles & Permissions"
              description="Enable role-based access control for team members"
              className="pl-4"
            />
          </div>

          <div className="text-default-600 text-sm">
            <p>
              When enabling Roles & Permissions, at least one team member must
              be assigned Admin. All other team members will still have
              full-access permissions until their role has been assigned. Assign
              and edit R&P in the{' '}
              <HeroLink
                as={Link}
                to={`/buyers/${buyer.id}?tab=team`}
                color="primary"
                size="sm"
              >
                Team
              </HeroLink>{' '}
              tab.
            </p>
          </div>
        </CardBody>
      </Card>

      {/* Disable Roles Modal */}
      <DisableRolesModal
        isOpen={showDisableModal}
        onClose={() => setShowDisableModal(false)}
        onConfirm={handleDisableRoles}
        isLoading={isSubmitting}
      />

      {/* Enable Roles Modal */}
      <EnableRolesModal
        isOpen={showEnableModal}
        onClose={() => setShowEnableModal(false)}
        onConfirm={handleEnableRoles}
        buyerId={buyer.id}
        buyerName={buyer.name}
        buyerReferenceId={buyer.referenceId || buyer.id}
        isLoading={isSubmitting}
      />
    </div>
  );
};
